// Simple alert system that works without complex dependencies
let alertContainer = null;
let referralContainer = null; // Separate container for top-left alerts

const createAlertContainer = () => {
  if (!alertContainer) {
    alertContainer = document.createElement('div');
    alertContainer.id = 'alert-container';
    alertContainer.className = 'fixed top-4 right-4 z-50 space-y-2';
    document.body.appendChild(alertContainer);
  }
  return alertContainer;
};

const showNotification = (variant, title, message) => {
  const container = createAlertContainer();
  
  // Create alert element
  const notification = document.createElement('div');
  notification.className = `max-w-md p-4 rounded-lg border shadow-lg transition-all duration-300 transform translate-x-0 opacity-100`;
  
  // Set variant-specific styles
  const variantStyles = {
    success: 'border-green-200 bg-green-50 text-green-900',
    error: 'border-red-200 bg-red-50 text-red-900',
    warning: 'border-yellow-200 bg-yellow-50 text-yellow-900',
    info: 'border-blue-200 bg-blue-50 text-blue-900'
  };
  
  notification.className += ` ${variantStyles[variant] || variantStyles.info}`;
  
  // Set variant-specific icons
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  };
  
  notification.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0 w-5 h-5 flex items-center justify-center text-lg font-bold">
        ${icons[variant] || icons.info}
      </div>
      <div class="flex-1">
        <h5 class="font-medium">${title}</h5>
        <div class="text-sm opacity-90">${message}</div>
      </div>
      <button class="flex-shrink-0 rounded-full p-1 hover:bg-black/5 transition-colors" onclick="this.parentElement.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  `;
  
  // Add to container
  container.appendChild(notification);
  
  // Auto remove after 5 seconds
  setTimeout(() => {
    if (notification.parentElement) {
      notification.style.transform = 'translateX(100%)';
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 300);
    }
  }, 5000);
};

// Verification alert with action buttons (matches alert.info style)
const showVerificationAlert = (verificationStatus, onVerify, onLater) => {
  const container = createAlertContainer();
  
  // Remove any existing verification alerts
  const existing = container.querySelector('.verification-alert');
  if (existing) {
    existing.remove();
  }
  
  // Create verification alert element - matching alert.info style
  const notification = document.createElement('div');
  notification.className = 'verification-alert max-w-md p-4 rounded-lg border shadow-lg transition-all duration-300 transform translate-x-0 opacity-100 border-blue-200 bg-blue-50 text-blue-900';
  
  const emailStatus = verificationStatus.isEmailVerified 
    ? '✓ Email verified'
    : '✕ Email verification pending';
  
  const phoneStatus = verificationStatus.isPhoneVerified
    ? '✓ Phone verified'
    : '✕ Phone verification pending';
  
  const addressStatus = verificationStatus.isAddressVerified
    ? '✓ Address added'
    : '✕ Address not added';
  
  notification.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0 w-5 h-5 flex items-center justify-center text-lg font-bold">
        ℹ
      </div>
      <div class="flex-1">
        <h5 class="font-medium">Verification Required</h5>
        <div class="text-sm opacity-90 mt-1 mb-2">Complete verification to access all features</div>
        <div class="text-xs space-y-1 mb-3">
          <div>${emailStatus}</div>
          <div>${phoneStatus}</div>
          <div>${addressStatus}</div>
        </div>
        <div class="flex gap-2 mt-3">
          <button id="verify-later-btn" class="flex-1 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-900 text-xs font-medium rounded transition-colors">
            Later
          </button>
          <button id="verify-now-btn" class="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors">
            Verify Now
          </button>
        </div>
      </div>
      <button class="flex-shrink-0 rounded-full p-1 hover:bg-black/5 transition-colors" onclick="this.parentElement.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  `;
  
  // Add to container
  container.appendChild(notification);
  
  // Add event listeners
  const laterBtn = notification.querySelector('#verify-later-btn');
  const verifyBtn = notification.querySelector('#verify-now-btn');
  
  laterBtn.onclick = () => {
    // Remove notification first
    if (notification.parentElement) {
      notification.remove();
    }
    // Store timestamp in localStorage - user won't see this alert for 2 days
    try {
      const timestamp = Date.now();
      localStorage.setItem('verificationAlertDismissedAt', timestamp.toString());
    } catch (error) {
      // If localStorage is unavailable (rare), continue anyway
      console.warn('Could not save dismissal timestamp to localStorage');
    }
    if (onLater) onLater();
  };
  
  verifyBtn.onclick = () => {
    // Remove notification immediately
    if (notification.parentElement) {
      notification.remove();
    }
    // Also remove the container if it's empty
    const alertContainer = document.getElementById('alert-container');
    if (alertContainer && alertContainer.children.length === 0) {
      alertContainer.remove();
    }
    // Call the verify callback after a tiny delay to ensure DOM cleanup
    setTimeout(() => {
      if (onVerify) onVerify();
    }, 50);
  };
  
  // Don't auto-remove this alert - user must click a button
};

// Referral code alert with Yes/No options (positioned top-left)
const showReferralCodeAlert = (onYes, onNo) => {
  console.log("🎁 showReferralCodeAlert called");
  
  // Create a separate container for top-left alerts
  if (!referralContainer) {
    referralContainer = document.createElement('div');
    referralContainer.id = 'referral-alert-container';
    referralContainer.className = 'fixed top-4 left-4 z-50';
    document.body.appendChild(referralContainer);
    console.log("✅ Created referral-alert-container");
  }

  // Remove any existing referral alerts
  const existing = referralContainer.querySelector('.referral-alert');
  if (existing) {
    existing.remove();
  }

  // Create referral alert element
  const notification = document.createElement('div');
  notification.className = 'referral-alert max-w-md p-4 rounded-lg border shadow-lg transition-all duration-300 transform translate-x-0 opacity-100 border-purple-200 bg-purple-50 text-purple-900';
  
  console.log("✅ Created referral alert notification element");
  
  notification.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0 w-5 h-5 flex items-center justify-center text-lg font-bold">
        🎁
      </div>
      <div class="flex-1">
        <h5 class="font-medium">Have a Referral Code?</h5>
        <div class="text-sm opacity-90 mt-1 mb-3">Enter your referral code and earn rewards!</div>
        <div class="flex gap-2">
          <button id="referral-no-btn" class="flex-1 px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-900 text-xs font-medium rounded transition-colors">
            No Thanks
          </button>
          <button id="referral-yes-btn" class="flex-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded transition-colors">
            Yes, I Have One
          </button>
        </div>
      </div>
      <button class="flex-shrink-0 rounded-full p-1 hover:bg-black/5 transition-colors" onclick="this.parentElement.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  `;

  // Add to container
  referralContainer.appendChild(notification);
  console.log("✅ Appended referral notification to container");

  // Add event listeners
  const yesBtn = notification.querySelector('#referral-yes-btn');
  const noBtn = notification.querySelector('#referral-no-btn');

  yesBtn.onclick = () => {
    if (notification.parentElement) {
      notification.remove();
    }
    if (onYes) onYes();
  };

  noBtn.onclick = () => {
    if (notification.parentElement) {
      notification.remove();
    }
    if (onNo) onNo();
  };

  // Don't auto-remove this alert - user must click a button
};

// Referral code input alert (appears after user clicks "Yes")
const showReferralInputAlert = (onSubmit, onCancel) => {
  // Remove the Yes/No alert first
  const yesNoAlert = document.querySelector('.referral-alert');
  if (yesNoAlert) {
    yesNoAlert.remove();
  }

  // Create input alert element
  const notification = document.createElement('div');
  notification.className = 'referral-input-alert max-w-md p-4 rounded-lg border shadow-lg transition-all duration-300 transform translate-x-0 opacity-100 border-purple-200 bg-purple-50 text-purple-900';
  
  notification.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0 w-5 h-5 flex items-center justify-center text-lg font-bold">
        🎁
      </div>
      <div class="flex-1">
        <h5 class="font-medium mb-2">Enter Referral Code</h5>
        <input 
          type="text" 
          id="referral-code-input" 
          placeholder="Enter code" 
          class="w-full px-3 py-2 border border-purple-300 rounded text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
          maxlength="10"
        />
        <div id="referral-feedback" class="text-xs mb-2"></div>
        <div class="flex gap-2">
          <button id="referral-cancel-btn" class="flex-1 px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-900 text-xs font-medium rounded transition-colors">
            Cancel
          </button>
          <button id="referral-submit-btn" class="flex-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded transition-colors">
            Apply
          </button>
        </div>
      </div>
      <button class="flex-shrink-0 rounded-full p-1 hover:bg-black/5 transition-colors" onclick="this.parentElement.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  `;

  // Add to container
  if (!referralContainer) {
    referralContainer = document.createElement('div');
    referralContainer.id = 'referral-alert-container';
    referralContainer.className = 'fixed top-4 left-4 z-50';
    document.body.appendChild(referralContainer);
  }
  referralContainer.appendChild(notification);

  // Focus on input
  setTimeout(() => {
    const input = notification.querySelector('#referral-code-input');
    if (input) input.focus();
  }, 100);

  // Add event listeners
  const submitBtn = notification.querySelector('#referral-submit-btn');
  const cancelBtn = notification.querySelector('#referral-cancel-btn');
  const input = notification.querySelector('#referral-code-input');

  const handleSubmit = () => {
    const code = input.value.trim();
    if (onSubmit) onSubmit(code);
  };

  submitBtn.onclick = handleSubmit;
  
  input.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  });

  cancelBtn.onclick = () => {
    if (notification.parentElement) {
      notification.remove();
    }
    if (onCancel) onCancel();
  };

  // Don't auto-remove this alert
};

// Export the alert functions
export const alert = {
  success: (message, title = "Success") => {
    showNotification('success', title, message);
  },
  error: (message, title = "Error") => {
    showNotification('error', title, message);
  },
  warning: (message, title = "Warning") => {
    showNotification('warning', title, message);
  },
  info: (message, title = "Information") => {
    showNotification('info', title, message);
  },
  // Default alert function
  default: (message, title = "Alert") => {
    showNotification('info', title, message);
  },
  // Verification alert with action buttons
  verification: (verificationStatus, onVerify, onLater) => {
    showVerificationAlert(verificationStatus, onVerify, onLater);
  },
  // Referral code alert
  referral: {
    ask: (onYes, onNo) => {
      showReferralCodeAlert(onYes, onNo);
    },
    input: (onSubmit, onCancel) => {
      showReferralInputAlert(onSubmit, onCancel);
    }
  }
};

// Hook-based alert for React components (simplified version)
export const useAlert = () => {
  return {
    success: (message, title = "Success") => {
      showNotification('success', title, message);
    },
    error: (message, title = "Error") => {
      showNotification('error', title, message);
    },
    warning: (message, title = "Warning") => {
      showNotification('warning', title, message);
    },
    info: (message, title = "Information") => {
      showNotification('info', title, message);
    },
    alert: (message, title = "Alert") => {
      showNotification('info', title, message);
    },
    verification: (verificationStatus, onVerify, onLater) => {
      showVerificationAlert(verificationStatus, onVerify, onLater);
    },
    // Referral code alert
    referral: {
      ask: (onYes, onNo) => {
        showReferralCodeAlert(onYes, onNo);
      },
      input: (onSubmit, onCancel) => {
        showReferralInputAlert(onSubmit, onCancel);
      }
    }
  };
};
